import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";

const SHIPPING_METHOD_OPTIONS = [
  { value: "NOT_SELECTED", label: "No Seleccionado" },
  { value: "express", label: "Envío Express" },
  { value: "same_day", label: "Envío Same day" },
  { value: "regional", label: "Envío Regional" },
  { value: "rm_rural", label: "Envío RM Rural" },
  { value: "pod", label: "Envío a Punto de Despacho" },
  { value: "rm_same_day", label: "Envío RM Same day" },
  { value: "rm_next_day", label: "Envío RM Next day" },
  { value: "meli", label: "Envío Meli" },
  { value: "meli_flex", label: "Envío Meli Flex" },
  { value: "meli_places", label: "Envío Meli Places" },
  { value: "region", label: "Envío Región" },
  { value: "pick_up_goex", label: "Pick Up GO-EX" },
  { value: "pick_up_bluex", label: "Pick Up Bluex" },
  { value: "devolution_bluex", label: "Devolución Bluex" },
  { value: "not_selected", label: "Not Selected" },
  { value: "express_uber", label: "Envío Express Uber" },
];

/**
 * @typedef {object} SelectOption
 * @property {string|number} value - The value to be submitted
 * @property {string} label - The human-readable text to display
 */

/**
 * Hook to fetch all necessary <select> options for shipping zone forms
 *
 * Fetches warehouse, commune, and courier options from the API in parallel
 * on mount. Also provides a static list of shipping methods
 *
 * @returns {{
 * loadingOptions: boolean,
 * errorOptions: string | null,
 * warehouseOptions: Array<SelectOption>,
 * communeOptions: Array<SelectOption>,
 * courierOptions: Array<SelectOption>,
 * adminOptions: Array<SelectOption>,
 * methodOptions: Array<SelectOption>
 * }} An object containing all lists of options and their loading/error state
 *
 * @example
 * const { loadingOptions, warehouseOptions, methodOptions } = useShippingZoneOptions();
 */
export function useShippingZoneOptions() {
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [communeOptions, setCommuneOptions] = useState([]);
  const [courierOptions, setCourierOptions] = useState([]);
  const [adminOptions, setAdminOptions] = useState([]);
  const [methodOptions] = useState(SHIPPING_METHOD_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [loadingCommunes, setLoadingCommunes] = useState(true);
  const [error, setError] = useState(null);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [warehouseRes, courierRes, adminRes] = await Promise.all([
        axios.get(`warehouses/`),
        axios.get("/shipping_zones/options/couriers/"),
        axios.get("admins/list/"),
      ]);

      const whOptions = (warehouseRes.data.warehouses || []).map((warehouse) => ({
        value: warehouse.id,
        label: `${warehouse.name} (${warehouse?.company?.social_reason})`,
      }));

      const adOptions = adminRes.data.map((admin) => ({
        value: admin.id,
        label: `${admin.user?.email} (${admin.user?.first_name} ${admin.user?.last_name})`,
      }));
      setAdminOptions(adOptions);
      setWarehouseOptions(whOptions);
      setCourierOptions(courierRes.data);
    } catch (error) {
      console.error(`Error al cargar información para selectores: ${error}`);
      setError("Error al cargar información para selectores");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCommunes = useCallback(async (countryIso) => {
    if (!countryIso) return;

    setLoadingCommunes(true);
    try {
      const { data } = await axios.get(`/shipping_zones/options/communes/?country=${countryIso}`);
      setCommuneOptions(data);
    } catch (error) {
      console.error(`Error loading communes for ${countryIso}: ${error}`);
      setError(`Error al cargar comunas para el país seleccionado`);
    } finally {
      setLoadingCommunes(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return {
    loadingOptions: loading,
    loadingCommunes,
    errorOptions: error,
    warehouseOptions,
    communeOptions,
    courierOptions,
    adminOptions,
    methodOptions,
    fetchCommunes
  };
}
